// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import { Address, type AddressWireFormat, NewAddress } from '../Address/Address';
import { type CostRule, type CostRuleWireFormat, NewCostRule } from '../CostRule/CostRule';
import {
  DeliveryCost,
  type DeliveryCostWireFormat,
  NewDeliveryCost,
} from '../DeliveryCost/DeliveryCost';
import Enums from '../Enums';
import { type Forecast, type ForecastWireFormat, NewForecast } from '../Forecast/Forecast';
import {
  type IntegrationSummary,
  type IntegrationSummaryWireFormat,
  NewIntegrationSummary,
} from '../IntegrationSummary/IntegrationSummary';
import { Market, type MarketWireFormat, NewMarket } from '../Market/Market';
import {
  NewPlantCustomField,
  type PlantCustomField,
  type PlantCustomFieldWireFormat,
} from '../PlantCustomField/PlantCustomField';
import {
  NewReadyMixConstants,
  type ReadyMixConstants,
  type ReadyMixConstantsWireFormat,
} from '../ReadyMixConstants/ReadyMixConstants';

export class Plant {
  readonly id: string;
  readonly name: string;
  readonly notes: string | null;
  readonly address: Address;
  readonly category: Enums.PlantCategory;
  readonly readyMixConstants: ReadyMixConstants | null;
  readonly currencyCode: string;
  readonly forecasts: Forecast[];
  readonly deliveryCosts: DeliveryCost;
  readonly materialCostRule: CostRule | null;
  readonly market: Market;
  readonly customFields: PlantCustomField[];
  readonly integrationSummary: IntegrationSummary | null;
  readonly externalID: string | null;
  readonly isArchived: boolean;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.notes = data.notes === undefined ? null : data.notes;
    this.address = NewAddress(data.address);
    this.category = data.category === undefined ? Enums.PlantCategory.ReadyMix : data.category;
    this.readyMixConstants =
      // eslint-disable-next-line no-nested-ternary
      (data.readyMixConstants ?? null) === null
        ? null
        : NewReadyMixConstants(data.readyMixConstants);
    this.currencyCode = data.currencyCode === undefined ? '' : data.currencyCode;
    this.forecasts = (data.forecasts ?? []).map((forecasts: any) => NewForecast(forecasts));
    this.deliveryCosts = NewDeliveryCost(data.deliveryCosts);
    this.materialCostRule =
      // eslint-disable-next-line no-nested-ternary
      (data.materialCostRule ?? null) === null ? null : NewCostRule(data.materialCostRule);
    this.market = NewMarket(data.market);
    this.customFields = (data.customFields ?? []).map((customFields: any) =>
      NewPlantCustomField(customFields),
    );
    this.integrationSummary =
      // eslint-disable-next-line no-nested-ternary
      (data.integration ?? data.integrationSummary ?? null) === null
        ? null
        : NewIntegrationSummary(data.integration ?? data.integrationSummary);
    this.externalID =
      // eslint-disable-next-line no-nested-ternary
      (data.externalID ?? null) === null
        ? null
        : data.externalID === undefined
          ? ''
          : data.externalID;
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
  }

  static zero(): Plant {
    const zeroValues: ExcludeMethodsDeep<Plant> = {
      id: NIL_UUID,
      name: '',
      notes: null,
      address: Address.zero(),
      category: Enums.PlantCategory.ReadyMix,
      readyMixConstants: null,
      currencyCode: '',
      forecasts: [],
      deliveryCosts: DeliveryCost.zero(),
      materialCostRule: null,
      market: Market.zero(),
      customFields: [],
      integrationSummary: null,
      externalID: null,
      isArchived: false,
    };
    return new Plant(zeroValues);
  }

  toJSON(): { [key: string]: any } {
    return {
      id: this.id,
      name: this.name,
      notes: this.notes,
      address: this.address,
      category: this.category,
      readyMixConstants: this.readyMixConstants,
      currencyCode: this.currencyCode,
      forecasts: this.forecasts,
      deliveryCosts: this.deliveryCosts,
      materialCostRule: this.materialCostRule,
      market: this.market,
      customFields: this.customFields,
      integration: this.integrationSummary,
      externalID: this.externalID,
      isArchived: this.isArchived,
    };
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type PlantWireFormat = {
  id: string;
  name: string;
  notes: string | null;
  address: AddressWireFormat;
  category: string;
  readyMixConstants: ReadyMixConstantsWireFormat | null;
  currencyCode: string;
  forecasts: ForecastWireFormat[];
  deliveryCosts: DeliveryCostWireFormat;
  materialCostRule: CostRuleWireFormat | null;
  market: MarketWireFormat;
  customFields: PlantCustomFieldWireFormat[];
  integrationSummary: IntegrationSummaryWireFormat | null;
  externalID: string | null;
  isArchived: boolean;
};

type NewPlantProps = PartialDeep<Plant, { recurseIntoArrays: true }>;
export const NewPlant: (props: NewPlantProps) => Plant = (props) => new Plant(props);

type NewPlantFromWireFormatProps = PartialDeep<PlantWireFormat, { recurseIntoArrays: true }>;
export const NewPlantFromWireFormat: (props: NewPlantFromWireFormatProps) => Plant = (props) =>
  new Plant(props);
